import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, CalendarClock, Check, CheckCircle2, CircleAlert, FileText, FlaskConical, Info, Languages, Plus, Search, Send, ShieldCheck, Tags, UserRoundCheck, UsersRound,
} from 'lucide-react';
import tagService from 'src/services/tagService';
import contactService from 'src/features/Contact/services/contactService';
import { WsDropdown, WsSegmented } from 'src/stage/WsControls';
import { Monogram, PageHeader, Skeleton, StagePage, WsDialog, formatNumber, formatWhen } from 'src/stage/StageUi';
import broadcastApi, { currentConnection, readErrors } from './broadcastApi';
import {
  SOURCES, audiencePayload, audienceReady, defaultVariables, localInputValue, newIdempotencyKey, tierLimit, variableErrors,
} from './broadcastModel';
import MessagePreview from './MessagePreview';
import useConnectedNumber from 'src/stage/useConnectedNumber';
import './broadcasts.css';

const AUDIENCE_MODES = [
  { id: 'all', label: 'Everyone', icon: UsersRound },
  { id: 'tags', label: 'By tag', icon: Tags },
  { id: 'contacts', label: 'Choose people', icon: UserRoundCheck },
];
const WHEN = [{ id: 'now', label: 'Send now', icon: Send }, { id: 'schedule', label: 'Schedule', icon: CalendarClock }];
const MATCH = [{ id: 'any', label: 'Any tag' }, { id: 'all', label: 'All tags' }];
const prettyName = (name = '') => name.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
const bodyText = (template) => (template?.components || []).find((c) => String(c.type).toUpperCase() === 'BODY')?.text || '';

function Section({ number, title, hint, children, id }) {
  return (
    <section className="ws-card wa-bc-section" aria-labelledby={id}>
      <div className="wa-bc-section-head">
        <span className="wa-step-num" aria-hidden="true">{number}</span>
        <div className="ws-grow">
          <h2 className="ws-h3" id={id}>{title}</h2>
          {hint && <p className="ws-muted" style={{ marginTop: 2 }}>{hint}</p>}
        </div>
      </div>
      <div className="wa-bc-section-body">{children}</div>
    </section>
  );
}

function TemplatePicker({ templates, loading, value, onChange, error }) {
  if (loading) return <div style={{ display: 'grid', gap: 10 }}>{[0, 1, 2].map((i) => <Skeleton key={i} height={62} radius={14} />)}</div>;
  if (!templates.length) {
    return (
      <div className="ws-empty">
        <strong>No approved templates yet</strong>
        Broadcasts need a template that Meta has approved.
        <Link to="/templates/create" className="ws-btn ws-btn--quiet ws-btn--sm" style={{ marginTop: 8 }}><Plus size={14} />Create a template</Link>
      </div>
    );
  }
  return (
    <>
      <div className="wa-tpl-grid" role="radiogroup" aria-label="Approved templates">
        {templates.map((template) => {
          const selected = template.id === value;
          return (
            <button key={template.id} type="button" role="radio" aria-checked={selected} disabled={Boolean(template.unsupported)}
              className={`wa-tpl${selected ? ' is-selected' : ''}`} onClick={() => onChange(template)} title={template.unsupported || undefined}>
              <span className="wa-tpl-top">
                <strong className="wa-ellipsis">{prettyName(template.name)}</strong>
                {selected && <span className="wa-tpl-check" aria-hidden="true"><Check size={12} strokeWidth={3} /></span>}
              </span>
              <span className="wa-tpl-text">{template.unsupported || bodyText(template)}</span>
              <span className="wa-tpl-meta">
                <span className={`wa-cat wa-cat--${String(template.category).toLowerCase()}`}>{String(template.category || '').toLowerCase()}</span>
                <span className="ws-chip"><Languages size={12} aria-hidden="true" />{template.language}</span>
              </span>
            </button>
          );
        })}
      </div>
      {error && <p className="wa-field-error" role="alert">{error}</p>}
      <p className="wa-help" style={{ marginTop: 10 }}>Only approved templates are listed. <Link to="/templates" style={{ color: '#0F6392', fontWeight: 550 }}>Manage templates</Link></p>
    </>
  );
}

function VariableRow({ slot, rule, onChange, error }) {
  const fixedOnly = slot.kind !== 'text';
  const options = SOURCES.filter((source) => !fixedOnly || source.value === 'text').map((source) => ({ value: source.value, label: source.label }));
  const needsFallback = SOURCES.find((source) => source.value === rule?.source)?.needsFallback;
  return (
    <div className="wa-var">
      <div className="wa-var-label">
        <span className="wa-var-key">{slot.key === 'media' ? slot.kind : `{{${slot.key}}}`}</span>
        <span className="ws-muted">{slot.label}</span>
      </div>
      <div className="wa-var-inputs">
        {!fixedOnly && <WsDropdown label="Fill with" value={rule?.source || 'text'} options={options} onChange={(source) => onChange({ ...rule, source })} />}
        {rule?.source === 'text' && (
          <input className="ws-input wa-input-sm" value={rule.value || ''} onChange={(event) => onChange({ ...rule, value: event.target.value })}
            placeholder={slot.key === 'media' ? 'https://… public link' : 'Text everyone gets'} aria-label={`Text for ${slot.label}`} maxLength={1000} />
        )}
        {needsFallback && (
          <input className="ws-input wa-input-sm" value={rule.fallback || ''} onChange={(event) => onChange({ ...rule, fallback: event.target.value })}
            placeholder="If there's no name, e.g. there" aria-label={`Fallback for ${slot.label}`} maxLength={60} />
        )}
      </div>
      {error && <p className="wa-field-error" role="alert">{error}</p>}
    </div>
  );
}

function TagChips({ tags, selected, onToggle, label }) {
  if (!tags.length) return <p className="wa-help">No tags yet. <Link to="/tags" style={{ color: '#0F6392', fontWeight: 550 }}>Create tags</Link> to group contacts.</p>;
  return (
    <div className="wa-chips" role="group" aria-label={label}>
      {tags.map((tag) => {
        const on = selected.includes(tag.id);
        return (
          <button key={tag.id} type="button" className={`wa-tagchip${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => onToggle(tag.id)}>
            <i style={{ background: tag.color || '#147DB5' }} aria-hidden="true" />{tag.name}
            {tag.contacts_count !== undefined && <span className="ws-num">{tag.contacts_count}</span>}
          </button>
        );
      })}
    </div>
  );
}

function ContactPicker({ connection, selected, onChange }) {
  const [query, setQuery] = useState('');
  const [state, setState] = useState({ loading: true, rows: [] });
  useEffect(() => {
    let active = true;
    const timer = setTimeout(() => {
      setState((current) => ({ ...current, loading: true }));
      contactService.index(connection.workspaceId, connection.whatsappAccountId, connection.phoneNumberId, { search: query || undefined, per_page: 50, is_blocked: 0 })
        .then((response) => { if (active) setState({ loading: false, rows: response.data?.data || [] }); })
        .catch(() => { if (active) setState({ loading: false, rows: [], error: true }); });
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [connection, query]);
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((item) => item !== id) : [...selected, id]);
  return (
    <div className="wa-picker">
      <div className="wa-picker-bar">
        <label className="ws-searchbox" style={{ flex: '1 1 auto', maxWidth: 'none' }}>
          <Search size={15} aria-hidden="true" />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search by name or number" aria-label="Search contacts" />
        </label>
        <span className="ws-chip"><span className="ws-num">{selected.length}</span> chosen</span>
        {selected.length > 0 && <button type="button" className="ws-btn ws-btn--plain" onClick={() => onChange([])}>Clear</button>}
      </div>
      <ul className="wa-picker-list" aria-label="Contacts">
        {state.loading && [0, 1, 2, 3].map((i) => <li key={i} style={{ padding: '8px 10px' }}><Skeleton height={30} /></li>)}
        {!state.loading && !state.rows.length && <li className="ws-muted" style={{ padding: 14 }}>{state.error ? 'Contacts could not be loaded.' : 'No contacts match.'}</li>}
        {!state.loading && state.rows.map((contact) => {
          const on = selected.includes(contact.id);
          return (
            <li key={contact.id}>
              <button type="button" className={`wa-picker-item${on ? ' is-on' : ''}`} aria-pressed={on} onClick={() => toggle(contact.id)} disabled={contact.is_blocked}>
                <span className={`wa-check${on ? ' is-on' : ''}`} aria-hidden="true">{on && <Check size={12} strokeWidth={3} />}</span>
                <Monogram name={contact.name || '#'} size={30} />
                <span className="ws-grow" style={{ minWidth: 0 }}>
                  <strong className="wa-ellipsis" style={{ display: 'block' }}>{contact.name || 'No name saved'}</strong>
                  <span className="ws-muted">{contact.phone_number}{contact.is_blocked ? ' · blocked' : ''}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function useAudiencePreview(phoneNumberId, draft) {
  const [state, setState] = useState({ loading: false, value: null, error: null });
  const payload = useMemo(() => (audienceReady(draft) ? JSON.stringify(audiencePayload(draft)) : null), [draft]);
  useEffect(() => {
    if (!payload) { setState({ loading: false, value: null, error: null }); return undefined; }
    let active = true;
    setState((current) => ({ ...current, loading: true }));
    const timer = setTimeout(() => {
      broadcastApi.audience(phoneNumberId, JSON.parse(payload))
        .then((value) => { if (active) setState({ loading: false, value, error: null }); })
        .catch((error) => { if (active) setState({ loading: false, value: null, error: readErrors(error) }); });
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [phoneNumberId, payload]);
  return state;
}

const TEST_KEY = 'autoappy.whatsapp.testNumber';

// Sends the personalised message to one number (usually your own) first.
function TestSend({ phoneNumberId, template, slots, variables, disabled }) {
  const [phone, setPhone] = useState(() => { try { return window.localStorage.getItem(TEST_KEY) || ''; } catch { return ''; } });
  const [state, setState] = useState({ busy: false, done: '', error: '' });
  const send = async (event) => {
    event.preventDefault();
    setState({ busy: true, done: '', error: '' });
    try {
      const result = await broadcastApi.test(phoneNumberId, {
        template_id: template.id,
        variables: Object.fromEntries(slots.map((slot) => [slot.slot, variables[slot.slot]])),
        phone_no: phone.replace(/[\s-]/g, ''),
      });
      try { window.localStorage.setItem(TEST_KEY, phone); } catch { /* private mode */ }
      setState({ busy: false, done: `Sent to ${result.phone_number}. Check WhatsApp on that phone.`, error: '' });
    } catch (error) {
      const parsed = readErrors(error);
      setState({ busy: false, done: '', error: parsed.fields.phone_no || Object.values(parsed.fields)[0] || parsed.summary });
    }
  };
  return (
    <form className="wa-bc-test" onSubmit={send}>
      <span className="ws-label"><FlaskConical size={14} aria-hidden="true" />Send a test to yourself first</span>
      <div className="wa-bc-test-row">
        <input className="ws-input wa-input-sm" type="tel" value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="+91 98765 43210" aria-label="Your WhatsApp number" disabled={state.busy} />
        <button type="submit" className="ws-btn ws-btn--quiet ws-btn--sm" disabled={disabled || state.busy || !phone.trim()}>{state.busy ? 'Sending…' : 'Send test'}</button>
      </div>
      {disabled && <span className="wa-help">Choose a template and fill its blanks to send a test.</span>}
      {state.done && <span className="wa-bc-test-ok" role="status"><CheckCircle2 size={14} aria-hidden="true" />{state.done}</span>}
      {state.error && <span className="wa-field-error" role="alert">{state.error}</span>}
    </form>
  );
}

function initialDraft(from) {
  const inHour = new Date(Date.now() + 60 * 60 * 1000);
  inHour.setMinutes(0, 0, 0);
  const tagIds = (from?.audience?.tags || []).map((tag) => tag.id);
  return {
    name: from ? `${from.name} (again)` : '',
    templateId: from?.template?.id || '',
    variables: from?.variables && !Array.isArray(from.variables) ? from.variables : {},
    // Chosen contacts are not copied: "Send again" asks for them, never widens to everyone.
    mode: from?.audience?.mode || 'all',
    tagIds,
    match: from?.audience?.match || 'any',
    excludeTagIds: (from?.audience?.excluded_tags || []).map((tag) => tag.id),
    contactIds: [],
    when: 'now',
    scheduledAt: localInputValue(inHour),
  };
}

export default function BroadcastComposer() {
  const navigate = useNavigate();
  const location = useLocation();
  // One object per visit: a new one on every render made the contact list reload after each tick.
  const connection = useMemo(() => currentConnection(), []);
  const phoneNumberId = connection?.phoneNumberId;
  // Contacts → "Send a broadcast" arrives with the chosen people.
  const [draft, setDraft] = useState(() => (location.state?.contactIds?.length
    ? { ...initialDraft(null), mode: 'contacts', contactIds: location.state.contactIds }
    : initialDraft(location.state?.from)));
  const [templates, setTemplates] = useState({ loading: true, list: [], error: null });
  const [tags, setTags] = useState([]);
  const [showExclude, setShowExclude] = useState(Boolean(draft.excludeTagIds.length));
  const [touched, setTouched] = useState(false);
  const [review, setReview] = useState(false);
  const [sending, setSending] = useState(false);
  const [serverErrors, setServerErrors] = useState({ summary: '', fields: {} });
  const idempotencyKey = useRef(newIdempotencyKey());
  const preview = useAudiencePreview(phoneNumberId, draft);
  const number = useConnectedNumber(phoneNumberId);
  const set = useCallback((patch) => setDraft((current) => ({ ...current, ...patch })), []);

  useEffect(() => {
    let active = true;
    broadcastApi.templates(phoneNumberId)
      .then((list) => { if (active) setTemplates({ loading: false, list, error: null }); })
      .catch((error) => { if (active) setTemplates({ loading: false, list: [], error: readErrors(error) }); });
    if (connection?.workspaceId) {
      tagService.list(connection.workspaceId).then((response) => { if (active) setTags(response.data?.data || []); }).catch(() => {});
    }
    return () => { active = false; };
  }, [phoneNumberId, connection?.workspaceId]);

  const template = templates.list.find((item) => item.id === draft.templateId) || null;
  const slots = useMemo(() => template?.slots || [], [template]);
  const chooseTemplate = (next) => set({ templateId: next.id, variables: { ...defaultVariables(next.slots || []), ...(next.id === draft.templateId ? draft.variables : {}) } });
  const toggleIn = (key) => (id) => set({ [key]: draft[key].includes(id) ? draft[key].filter((item) => item !== id) : [...draft[key], id] });

  const varErrors = variableErrors(slots, draft.variables);
  const count = preview.value?.count ?? null;
  const scheduled = draft.when === 'schedule' ? new Date(draft.scheduledAt) : null;
  const errors = {
    name: !draft.name.trim() ? 'Give it a name only your team sees.' : null,
    template: !template ? 'Choose a template.' : null,
    audience: !audienceReady(draft) ? (draft.mode === 'tags' ? 'Choose at least one tag.' : 'Choose at least one person.') : (count === 0 ? 'No one matches this audience.' : null),
    when: scheduled && (Number.isNaN(scheduled.getTime()) || scheduled <= new Date()) ? 'Pick a time in the future.' : null,
  };
  const ready = !errors.name && !errors.template && !errors.audience && !errors.when && !Object.keys(varErrors).length && count !== null && !preview.loading;
  const limit = tierLimit(preview.value?.messaging_limit_tier);
  const sample = { name: preview.value?.sample?.find((person) => person.name)?.name || 'Asha Rao', phone: preview.value?.sample?.[0]?.phone_number, business: number?.verified_name || number?.display_name || 'Your business' };

  const openReview = () => {
    setTouched(true);
    if (ready) setReview(true);
  };

  const submit = async () => {
    setSending(true);
    setServerErrors({ summary: '', fields: {} });
    try {
      const created = await broadcastApi.create(phoneNumberId, {
        name: draft.name.trim(),
        template_id: draft.templateId,
        variables: Object.fromEntries(slots.map((slot) => [slot.slot, draft.variables[slot.slot]])),
        audience: audiencePayload(draft),
        send: draft.when,
        scheduled_at: draft.when === 'schedule' ? new Date(draft.scheduledAt).toISOString() : undefined,
        idempotency_key: idempotencyKey.current,
      });
      navigate(`/broadcast/${created.id}`, { replace: true, state: { created: true } });
    } catch (error) {
      const parsed = readErrors(error);
      setServerErrors(parsed);
      setReview(false);
      // A validation error means nothing was created, so the next attempt is a new request.
      if (error?.response?.status === 422) idempotencyKey.current = newIdempotencyKey();
    } finally {
      setSending(false);
    }
  };

  const show = (key) => touched && errors[key];
  const serverVariableError = (slot) => serverErrors.fields[`variables.${slot}`];

  return (
    <StagePage className="wa-bc wa-bc-compose" label="New broadcast">
      <Link to="/broadcast" className="wa-back"><ArrowLeft size={15} aria-hidden="true" />All broadcasts</Link>
      <PageHeader eyebrow="Broadcasts" title="New broadcast" description="Pick an approved template, choose who gets it, then send now or schedule it." />

      {(serverErrors.summary || templates.error) && (
        <div className="wa-notice wa-notice--bad" role="alert"><CircleAlert size={16} /><span>{serverErrors.summary || templates.error.summary}</span></div>
      )}

      <div className="wa-bc-compose-grid">
        <div className="wa-bc-compose-main">
          <Section number={1} id="bc-name" title="Name" hint="Only your team sees this.">
            <input className="ws-input" value={draft.name} onChange={(event) => set({ name: event.target.value })} maxLength={120} placeholder="e.g. Diwali offer for VIP customers" aria-labelledby="bc-name" aria-invalid={Boolean(show('name') || serverErrors.fields.name)} />
            {(show('name') || serverErrors.fields.name) && <p className="wa-field-error">{serverErrors.fields.name || errors.name}</p>}
          </Section>

          <Section number={2} id="bc-message" title="Message" hint="WhatsApp only allows approved templates when you message people first.">
            <TemplatePicker templates={templates.list} loading={templates.loading} value={draft.templateId} onChange={chooseTemplate} error={show('template') || serverErrors.fields.template_id} />
            {slots.length > 0 && (
              <div className="wa-vars">
                <h3 className="ws-h3" style={{ fontSize: 14 }}>Personalise</h3>
                <p className="ws-muted" style={{ marginBottom: 6 }}>Fill each blank in the template for every person.</p>
                {slots.map((slot) => (
                  <VariableRow key={slot.slot} slot={slot} rule={draft.variables[slot.slot]} error={(touched && varErrors[slot.slot]) || serverVariableError(slot.slot)}
                    onChange={(rule) => set({ variables: { ...draft.variables, [slot.slot]: rule } })} />
                ))}
              </div>
            )}
          </Section>

          <Section number={3} id="bc-audience" title="Audience" hint="Blocked contacts are always left out.">
            <WsSegmented label="Who gets it" items={AUDIENCE_MODES} value={draft.mode} onChange={(mode) => set({ mode })} />
            {draft.mode === 'tags' && (
              <div className="wa-bc-sub">
                <div className="wa-bc-sub-head"><span className="ws-label">People tagged with</span>{draft.tagIds.length > 1 && <WsSegmented label="Match" items={MATCH} value={draft.match} onChange={(match) => set({ match })} />}</div>
                <TagChips tags={tags} selected={draft.tagIds} onToggle={toggleIn('tagIds')} label="Tags to include" />
              </div>
            )}
            {draft.mode === 'contacts' && connection && <div className="wa-bc-sub"><ContactPicker connection={connection} selected={draft.contactIds} onChange={(contactIds) => set({ contactIds })} /></div>}
            {draft.mode !== 'contacts' && tags.length > 0 && (
              <div className="wa-bc-sub">
                {!showExclude
                  ? <button type="button" className="ws-btn ws-btn--plain" onClick={() => setShowExclude(true)}><Plus size={14} />Leave out people with a tag</button>
                  : (<><span className="ws-label">Leave out people tagged with</span><TagChips tags={tags} selected={draft.excludeTagIds} onToggle={toggleIn('excludeTagIds')} label="Tags to leave out" /></>)}
              </div>
            )}
            {(show('audience') || serverErrors.fields.audience) && <p className="wa-field-error" role="alert">{serverErrors.fields.audience || errors.audience}</p>}
          </Section>

          <Section number={4} id="bc-when" title="When">
            <WsSegmented label="When to send" items={WHEN} value={draft.when} onChange={(when) => set({ when })} />
            {draft.when === 'schedule' && (
              <div className="wa-bc-sub" style={{ maxWidth: 320 }}>
                <label className="ws-label" htmlFor="bc-scheduled">Date and time</label>
                <input id="bc-scheduled" type="datetime-local" className="ws-input" value={draft.scheduledAt} min={localInputValue(new Date())} onChange={(event) => set({ scheduledAt: event.target.value })} />
                <span className="wa-help">Your time zone: {Intl.DateTimeFormat().resolvedOptions().timeZone}. Up to 60 days ahead.</span>
              </div>
            )}
            {(show('when') || serverErrors.fields.scheduled_at) && <p className="wa-field-error">{serverErrors.fields.scheduled_at || errors.when}</p>}
          </Section>
        </div>

        <aside className="wa-bc-compose-side" aria-label="Preview and summary">
          <MessagePreview template={template} variables={draft.variables} sample={sample} />
          <div className="ws-card wa-bc-summary">
            <div className="wa-bc-count">
              <span className="wa-bc-count-icon" aria-hidden="true"><UsersRound size={18} /></span>
              <span className="ws-grow">
                {preview.loading && <Skeleton width={120} height={22} />}
                {!preview.loading && count !== null && <strong className="ws-num">{formatNumber(count)} {count === 1 ? 'person' : 'people'}</strong>}
                {!preview.loading && count === null && <strong>Choose an audience</strong>}
                <span className="ws-muted" style={{ display: 'block' }}>
                  {preview.value?.blocked ? `${formatNumber(preview.value.blocked)} blocked left out` : 'will get this message'}
                </span>
              </span>
            </div>
            {preview.value?.sample?.length > 0 && (
              <div className="wa-bc-sample">
                {preview.value.sample.slice(0, 4).map((person) => <span key={person.id} className="ws-chip">{person.name || person.phone_number}</span>)}
                {count > 4 && <span className="ws-muted" style={{ fontSize: 12 }}>and {formatNumber(count - 4)} more</span>}
              </div>
            )}
            <TestSend phoneNumberId={phoneNumberId} template={template} slots={slots} variables={draft.variables} disabled={!template || Object.keys(varErrors).length > 0} />
            {limit && count > limit && (
              <div className="wa-notice wa-notice--warn"><Info size={15} /><span>Meta lets this number start about {formatNumber(limit)} new chats a day. People above that may not get it until tomorrow.</span></div>
            )}
            <button type="button" className="ws-btn ws-btn--primary ws-btn--lg" style={{ width: '100%' }} onClick={openReview} disabled={sending}>
              <Send size={16} />{draft.when === 'schedule' ? 'Review and schedule' : 'Review and send'}
            </button>
            {touched && !ready && <p className="wa-help" style={{ textAlign: 'center' }}>Fix the highlighted steps to continue.</p>}
          </div>
        </aside>
      </div>

      {review && (
        <WsDialog title={draft.when === 'schedule' ? 'Schedule this broadcast?' : 'Send this broadcast now?'} onClose={() => !sending && setReview(false)} wide>
          <dl className="wa-review">
            <div><dt><FileText size={15} />Template</dt><dd>{prettyName(template?.name)} · {template?.language}</dd></div>
            <div><dt><UsersRound size={15} />Audience</dt><dd>{formatNumber(count)} {count === 1 ? 'person' : 'people'}</dd></div>
            <div><dt><CalendarClock size={15} />When</dt><dd>{draft.when === 'schedule' ? formatWhen(scheduled) : 'Right away'}</dd></div>
          </dl>
          <div className="wa-notice wa-notice--info" style={{ marginTop: 14 }}><ShieldCheck size={15} /><span>Each person gets one message. Sent messages can’t be recalled; you can stop the rest while it’s sending.</span></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
            <button type="button" className="ws-btn ws-btn--quiet" data-close onClick={() => setReview(false)} disabled={sending}>Keep editing</button>
            <button type="button" className="ws-btn ws-btn--primary" onClick={submit} disabled={sending}>
              <Send size={15} />{sending ? 'Starting…' : draft.when === 'schedule' ? 'Schedule broadcast' : `Send to ${formatNumber(count)}`}
            </button>
          </div>
        </WsDialog>
      )}
    </StagePage>
  );
}
